import { useCallback, useState } from 'react';
import { pickLocalFiles } from '@shakkerai/util-file';
import { translate } from '@shakkerai/util-i18n';
import { notifyError } from '@shakkerai/pro-components';
import { MAX_FILE_SIZE } from '@shakkerai/pro-constants';
import { uploadImage } from '@shakkerai/pro-utils';
import { getLocalImageSize } from '@shakkerai/util-image';

export function usePickFile({
  accept = ['jpeg', 'jpg', 'png', 'webp'],
  maxCount = 1,
  maxFileSize = MAX_FILE_SIZE,
  disabled = false,
  onAfterPickFile,
} = {}) {
  const handlePickFile = useCallback(async () => {
    if (disabled) return;

    const [file] = await pickLocalFiles(accept, maxCount);

    if (!file) return;

    if (file.size > maxFileSize) {
      notifyError(translate('common_operation_failed'), translate('img_size_toast'));
      return;
    }

    onAfterPickFile?.(file);

    return file;
  }, [disabled, onAfterPickFile]);

  return {
    handlePickFile,
  };
}

export function useUploadFile({ toTempBucket = false, from = '', onSuccess, onError } = {}) {
  const [loading, setLoading] = useState(false);

  const handleUploadFile = useCallback(
    async (file) => {
      const result = {
        url: '',
        width: 512,
        height: 512,
      };
      try {
        setLoading(true);
        const [{ url }, { width, height }] = await Promise.all([
          uploadImage(file, toTempBucket, from),
          getLocalImageSize(file),
        ]);
        result.url = url;
        result.width = width;
        result.height = height;
        onSuccess?.(result);
      } catch (error) {
        onError?.(error);
      } finally {
        setLoading(false);
      }

      return result;
    },
    [toTempBucket, from, onSuccess, onError],
  );

  return {
    handleUploadFile,
    loading,
  };
}
